module Client.Admin

open Fable.Core
open Fable.Import
open Elmish
open Fable.React
open Fable.React.Props
open Shared.Domain
open Style
open System
open Fetch
open Pages
open Fable
open Shared
open Utils
open AdminShared

type Msg =
    | FetchError of exn
    | DismissSuccess
    | EditPageMsg of AdminEditPage.Msg
    | EditCategoryMsg of AdminEditCategory.Msg
    | EditUserMsg of AdminEditUsers.Msg
    | EditFileMsg of AdminEditFiles.Msg
    | HelpPacksMsg of AdminHelpPacks.Msg
    | ShowLicenses
    | HideLicenses
    | LogOut

type AdminAction =
    | NoAction
    | EditPageModel of AdminEditPage.Model
    | EditCategoryModel of AdminEditCategory.Model
    | EditUserModel of AdminEditUsers.Model
    | EditFilesModel of AdminEditFiles.Model
    | HelpPacksModel of AdminHelpPacks.Model

type Model = 
    { 
        User: JWT;
        Action: AdminAction;
        SuccessMessage: string option;
        ShowingLicenses: bool
    }

let init (user: JWT) actionName = 
    printfn "ACTION: %s" actionName
    let action, cmds = 
        match actionName with
        | "pages" -> 
            let m,cmd = AdminEditPage.init user
            EditPageModel m, Cmd.map EditPageMsg cmd
        | "categories" -> 
            let m,cmd = AdminEditCategory.init user
            EditCategoryModel m, Cmd.map EditCategoryMsg cmd
        | "files" -> 
            let m,cmd = AdminEditFiles.init user
            EditFilesModel m, Cmd.map EditFileMsg cmd
        | "users" -> 
            let m,cmd = AdminEditUsers.init user
            EditUserModel m, Cmd.map EditUserMsg cmd
        | "helppacks" -> 
            let m,cmd = AdminHelpPacks.init user
            HelpPacksModel m, Cmd.map HelpPacksMsg cmd
        | "" -> NoAction, []
        | _ -> failwithf "Failed to parse admin action %s" actionName
    {
        User = user 
        Action = action
        SuccessMessage = None
        ShowingLicenses = false
    }, cmds

let dispatchContent action dispatch =
    match action with
    | NoAction -> 
        [
            div [ ClassName "jumbotron" ] [
                h1 [] [ str "Administration" ]
                p [] [ str "Please choose an administrative option from the sidebar." ]
            ]
        ]
    | EditPageModel state -> AdminEditPage.view state (EditPageMsg >> dispatch)  
    | EditUserModel state -> AdminEditUsers.view state (EditUserMsg >> dispatch)              
    | EditCategoryModel state -> AdminEditCategory.view state (EditCategoryMsg >> dispatch)  
    | EditFilesModel state -> AdminEditFiles.view state (EditFileMsg >> dispatch) 
    | HelpPacksModel state -> AdminHelpPacks.view state (HelpPacksMsg >> dispatch)               

let private mapSuccessMessage current =
    function
    | Show message -> Some message
    | Hide -> None
    | Unchanged -> current

let update (msg:Msg) (model: Model) : Model*Cmd<Msg> =     
    match msg, model.Action with
    | DismissSuccess, _ ->
        { model with SuccessMessage = None }, []
    | EditPageMsg msg, EditPageModel m ->
        let m,cmd,success = AdminEditPage.update msg m
        let cmd = Cmd.map EditPageMsg cmd
        { model with Action = EditPageModel m; SuccessMessage = mapSuccessMessage model.SuccessMessage success }, cmd
    | EditCategoryMsg msg, EditCategoryModel m ->
        let m,cmd,success = AdminEditCategory.update msg m
        let cmd = Cmd.map EditCategoryMsg cmd
        { model with Action = EditCategoryModel m; SuccessMessage = mapSuccessMessage model.SuccessMessage success }, cmd
    | EditUserMsg msg, EditUserModel m ->
        let m,cmd,success = AdminEditUsers.update msg m
        let cmd = Cmd.map EditUserMsg cmd
        { model with Action = EditUserModel m; SuccessMessage = mapSuccessMessage model.SuccessMessage success }, cmd
    | EditFileMsg msg, EditFilesModel m ->
        let m,cmd,success = AdminEditFiles.update msg m
        let cmd = Cmd.map EditFileMsg cmd
        { model with Action = EditFilesModel m; SuccessMessage = mapSuccessMessage model.SuccessMessage success }, cmd
    | HelpPacksMsg msg, HelpPacksModel m ->
        let m,cmd,success = AdminHelpPacks.update msg m
        let cmd = Cmd.map HelpPacksMsg cmd
        { model with Action = HelpPacksModel m; SuccessMessage = mapSuccessMessage model.SuccessMessage success }, cmd
    | ShowLicenses, _ ->
        { model with ShowingLicenses = true }, []
    | HideLicenses, _ ->
        { model with ShowingLicenses = false }, []
    | LogOut, _ -> 
        Browser.Dom.console.log "going home"
        BrowserLocalStorage.delete "user"
        model, Cmd.batch [  Elmish.Navigation.Navigation.newUrl (toHash Page.Home) ]
    | FetchError exn, _ ->
        model,[]
    | _ -> failwith "unexpected combo"

let private linkTo (user: User) action icon path name =
    let cn =
        match action with 
        | EditPageModel _ when path = "pages" -> "selected"
        | EditCategoryModel _ when path = "categories" -> "selected"
        | EditUserModel _ when path = "users" -> "selected"
        | EditFilesModel _ when path = "files" -> "selected"
        | HelpPacksModel _ when path = "helppacks" -> "selected"
        | _ -> ""
    li [ClassName cn] [ a [ Href (toHash (Page.Admin (TenantRef.get user.Tenant, path)))] [ 
        span [ ClassName ("hidden-xs glyphicon glyphicon-" + icon)] []; 
        str (" " + name) 
    ] 
]

let private sidebar dispatch (user: User) action =
    div [ Id "adminActionPanel"; ClassName "col-sm-2 no-pad-lr" ] [
       ul [ ClassName "admin-list" ] [
           linkTo user action "list-alt" "pages" "Pages"
           linkTo user action "tags" "categories" "Categories"
           linkTo user action "user" "users" "Users"
           linkTo user action "upload" "files" "File Uploads"
           linkTo user action "asterisk" "helppacks" "Help Packs"
           li [ OnClick (fun _ -> dispatch LogOut)] [ 
               a [ jsVoid ] [ 
                    span [ 
                       ClassName ("hidden-xs glyphicon glyphicon-off")
                    ] []
                    str "Log out from admin section"
                ]
            ] 
       ]                     
    ]

let private navbar (user: User) =
    nav [ 
            ClassName "navbar navbar-default"
            Role "navigation"
        ] [
            fluidContainer [
                yield div [ ClassName "navbar-header" ] [
                    a [ 
                        ClassName "navbar-brand" 
                        Href <| toHash (Page.Admin(TenantRef.get user.Tenant, ""))
                    ] [
                        fa "user"
                        str ("Change Your Tomorrow Administration - " + TenantRef.get user.Tenant)
                    ]
                ]
            ]  
        ]

let view model (dispatch: Msg -> unit) =
    let { User = user; Action = action } = model
    let ret =
        [
            navbar user.Account
            div [ 
                Id "main"
                ClassName "container-fluid admin expanding-child expanding-parent no-pad-lr"
            ] [ 
                div [ ClassName "row expanding-child no-margin-lr" ] [
                    sidebar dispatch user.Account action
                    div [ 
                        Id "adminContentPanel"; 
                        ClassName "col-sm-10 no-pad-lr" 
                    ] [
                        div [ClassName "content"] (dispatchContent action dispatch)
                    ]
                ] 
            ]
            div [] [
                if model.ShowingLicenses then
                    yield! Client.Style.renderAttribution dispatch HideLicenses
            ]
            siteFooter None (fun _ -> dispatch ShowLicenses)
        ] 
    match model.SuccessMessage with
    | None -> ret
    | Some message ->
        modal "Success" dispatch DismissSuccess 
            [
                p [] [
                    str message
                ]
            ] [ 
                button [ 
                    ClassName "btn btn-success" 
                    OnClick (fun _ -> dispatch DismissSuccess)
                ] [ str "Dismiss" ]
            ]@ret
